import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProjectPhasesService } from '../services/phases.service';
import type { ProjectPhase, PhaseFilters } from '../types/project-phases.types';
import { useState } from 'react';
import { toast } from 'sonner';

export function useProjectPhases(companyId: string, initialFilters: PhaseFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<PhaseFilters>({
    status: 'all',
    type: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['project-phases', companyId, filters],
    queryFn: () => ProjectPhasesService.getPhases(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['project-phase-stats', companyId, filters.projectId],
    queryFn: () => ProjectPhasesService.getPhaseStats(companyId, filters.projectId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<ProjectPhase>) => ProjectPhasesService.createPhase(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-phases'] });
      toast.success('Khởi tạo giai đoạn thành công');
    },
  });

  return {
    phases: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data,
    isLoading: query.isLoading || statsQuery.isLoading,
    filters,
    setFilters,
    createPhase: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
  };
}

export function useProjectPhaseDetail(id?: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['project-phases', 'detail', id],
    queryFn: () => ProjectPhasesService.getPhaseDetail(id!),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (updates: Partial<ProjectPhase>) => ProjectPhasesService.updatePhase(id!, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-phases'] });
      queryClient.invalidateQueries({ queryKey: ['project-phases', 'detail', id] });
      toast.success('Cập nhật giai đoạn thành công');
    },
  });

  return {
    phase: query.data,
    isLoading: query.isLoading,
    updatePhase: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}
